import { getLocationLevelLabel } from "api-types/locations/locationDisplay";
import type { ClientLocationHydrated, ClientLocationWithLevel } from "api-types/locations/locationsResponses";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import CommunitiesTable from "lib/components/builders/communities/CommunitiesTable";
import NewLocationButton from "lib/components/builders/communities/NewLocationButton";
import {
  canExpandLocation,
  createDescendantMap,
  filterLocationsBySearch,
  getLocationHomePlanCountWithDescendants,
} from "lib/components/builders/communities/utils/locationUtils";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderLocationChildrenURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import { useMemo } from "react";
import Loading from "ui/components/Loading";

interface SubLocationsProps {
  parentLocation: ClientLocationWithLevel;
  builderId: string;
  locationName?: string;
  locationId: string;
  showColumnHeaders?: boolean;
  emptyCTA?: React.ReactNode;
  search?: string;
  setSearch?: (s: string) => void;
  forcedExpanded?: Record<string, boolean>;
  showArchived?: boolean;
  setShowArchived?: (show: boolean) => void;
}

export default function SubLocations({
  parentLocation,
  builderId,
  locationName,
  locationId,
  showColumnHeaders = true,
  emptyCTA,
  search,
  setSearch,
  forcedExpanded,
  showArchived = false,
  setShowArchived,
}: SubLocationsProps) {
  const { builder, user } = useUserContext();

  const {
    data: locationsData,
    mutate,
    loading,
    error,
  } = useFetch<ClientLocationHydrated[]>(
    getBuilderLocationChildrenURL({ builderId, locationId, includeArchived: true }),
    [builderId, locationId],
    { handleErrorLocally: true },
  );

  const descendantsByParent = useMemo(() => createDescendantMap(locationsData || []), [locationsData]);

  const homePlanCountsWithDescendants = useMemo(() => {
    if (!locationsData) {
      return {};
    }
    return getLocationHomePlanCountWithDescendants(locationsData);
  }, [locationsData]);

  const filteredLocations = useMemo(() => {
    const directChildren = (locationsData ?? [])
      .filter((location) => location.parentId === locationId)
      .sort((a, b) => a.name.localeCompare(b.name));

    return filterLocationsBySearch(directChildren, search || "", descendantsByParent, showArchived, forcedExpanded);
  }, [locationsData, search, locationId, forcedExpanded, showArchived]);

  if (error) {
    return <FetchErrorMessage fetchError={error} />;
  }

  if (loading || !locationsData || !builder || !user || !builderId) {
    return <Loading />;
  }

  const handleUpdateLocation = async (community: ClientLocationHydrated): Promise<void> => {
    if (!community || !locationsData) {
      return;
    }

    const newCommunities: ClientLocationHydrated[] = locationsData.map((c) => (c.id === community.id ? community : c));

    await mutate(newCommunities);
  };

  const showBuilderPortalEmptyCTA = emptyCTA ? (
    <BuilderPortalEmptyCTA>
      <div className="cta-container">
        <div className="cta-title">
          There are no locations under this {getLocationLevelLabel(parentLocation, builder?.locationSettings)}.
        </div>
        <p className="cta-text">Want to add another location?</p>
        <NewLocationButton buttonText="Add Location" builderId={builderId} parentLocation={parentLocation} />
      </div>
    </BuilderPortalEmptyCTA>
  ) : null;

  return (
    <>
      <CommunitiesTable
        locationName={locationName}
        locations={filteredLocations}
        onSuccess={handleUpdateLocation}
        builder={builder}
        search={search}
        setSearch={setSearch}
        forcedExpanded={forcedExpanded}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        homePlanCountsWithDescendants={homePlanCountsWithDescendants}
        getRowCanExpand={(row) => canExpandLocation(row.original, descendantsByParent, search, showArchived)}
        subTable={(row) => (
          <SubLocations
            parentLocation={row.original}
            builderId={builderId}
            locationName={row.original.name}
            locationId={row.original.id}
            showColumnHeaders={false}
            emptyCTA={null}
            search={search}
            setSearch={setSearch}
            forcedExpanded={forcedExpanded}
            showArchived={showArchived}
          />
        )}
        showColumnHeaders={showColumnHeaders}
        emptyCTA={showBuilderPortalEmptyCTA}
      />
    </>
  );
}

export function SubLocationNested(
  props: Omit<React.ComponentProps<typeof SubLocations>, "showColumnHeaders" | "emptyCTA">,
) {
  return <SubLocations {...props} showColumnHeaders={false} emptyCTA={null} />;
}
